import EditHeader from "common/components/EditHeader/EditHeader";
import HeaderButtonsEdit from "common/components/EditHeader/HeaderButtonsEdit";
import FormControl from "common/components/FormControl/FormControl";
import FormScrollError from "common/components/FormScrollError/FormScrollError";
import useRouter from "common/hooks/use-router";
import { useMemo, useState } from "react";
import { Form } from "react-final-form";
import { useTranslation } from "react-i18next";
import arrayMutators from "final-form-arrays";
import usePark from "common/hooks/usePark";
import EnclosureSelect from "components/select/EnclosureSelect/EnclosureSelect";
import { Enclosure } from "types/Enclosure";
import { User } from "types";
import ManagerSelect from "components/select/UserSelect/ManagerSelect";
import UserSelect from "components/select/UserSelect/UserSelect";
import useAuth from "common/hooks/useAuth";

function SectorForm({ onSubmit, sector }: any) {
  const { t } = useTranslation();
  const [errorMessage, setErrorMessage] = useState("");
  const { navigate } = useRouter();
  const { park: currentPark } = usePark();
  const { user: currentUser } = useAuth();
  const canManageSector = sector?.id
    ? (currentUser?.isAdmin ||
        currentUser?.isSuperadmin ||
        ((currentUser?.isManager || currentUser?.isObserver) &&
          currentUser?.administrationPermissions?.sector?.update)) ??
      false
    : (currentUser?.isAdmin ||
        currentUser?.isSuperadmin ||
        ((currentUser?.isManager || currentUser?.isObserver) &&
          currentUser?.administrationPermissions?.sector?.create)) ??
      false;

  const initialValues = useMemo(
    () =>
      sector?.id
        ? {
            ...sector,
            park: sector.park?.id ?? currentPark?.id,
            enclosures: sector.enclosures?.map((e: Enclosure) => e.id),
            managers: sector.managers?.map((u: User) => u.id),
            users: sector.users?.map((u: User) => u.id),
          }
        : {
            name: "",
            description: "",
            park: currentPark?.id,
          },
    [sector, currentPark]
  );

  const onSubmitWrapper = async (values: any) => {
    const res = await onSubmit(values);

    if (res.message) {
      setErrorMessage(res.message);
    }
  };

  return (
    <Form
      onSubmit={onSubmitWrapper}
      subscription={{ values: true, submitting: true, pristine: true }}
      initialValues={initialValues}
      mutators={{
        ...arrayMutators,
        setFieldValue: ([field]: any, state: any, utils: any) => {
          utils.changeValue(state, field.field, () => field.value);
        },
      }}
      render={({ handleSubmit, values }) => {
        return (
          <div className="mx-auto max-w-full py-4 px-4 text-slate-500 text-base">
            {/* Header */}
            <EditHeader
              title={t(sector?.id ? "editItem.sector" : "newItem.sector")}
              subtitle={values.name}
              icon="PiTreePalmLight"
              errorMessage={errorMessage}
              HeaderButtonsEdit={
                <HeaderButtonsEdit
                  inHeader
                  onSubmit={canManageSector ? handleSubmit : undefined}
                  onCancel={() => navigate("/sectors")}
                />
              }
            />
            <form onSubmit={handleSubmit} noValidate>
              <FormScrollError />

              <div className="responsive-flex-lg gap-2 mt-2">
                <div className="lg:flex-1 flex flex-col gap-2 white-box">
                  <div className="flex flex-row gap-4">
                    <div className="flex flex-col w-full gap-2">
                      <FormControl
                        name="name"
                        title={t("common.name")}
                        required
                        editable={canManageSector}
                      />
                    </div>
                    <div className="flex flex-col w-full gap-2">
                      <FormControl
                        name="enclosures"
                        title={t("forms.enclosures")}
                        type="custom"
                        editable={canManageSector}
                        // required
                      >
                        <EnclosureSelect multiple />
                      </FormControl>
                    </div>
                  </div>
                  <div className="flex flex-row gap-4 w-full">
                    <div className="flex flex-col w-full gap-2">
                      <FormControl
                        name="description"
                        title={t("forms.description")}
                        editable={canManageSector}
                      />
                    </div>
                  </div>
                  <div className="flex flex-row gap-4 w-full">
                    <div className="flex flex-col w-full gap-2">
                      <FormControl
                        name="managers"
                        title={t("common.managers")}
                        type="custom"
                        editable={canManageSector}
                      >
                        <ManagerSelect multiple />
                      </FormControl>
                      <FormControl
                        type="custom"
                        title={t("forms.otherUsersAssigned")}
                        name="users"
                        editable={canManageSector}
                      >
                        <UserSelect multiple />
                      </FormControl>
                    </div>
                  </div>
                </div>
              </div>

              <HeaderButtonsEdit
                onSubmit={handleSubmit}
                onCancel={() => navigate("/sectors")}
              />
            </form>
          </div>
        );
      }}
    />
  );
}

export default SectorForm;
